import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  TextField,
  Divider,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { 
  useGetTradeInByIdQuery, 
  useUpdateTradeInMutation,
  useCompleteTradeInMutation 
} from '../../../store/api/tradeInApi';
import { useGetProductsQuery } from '../../../store/api/productsApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';

const STATUS_COLORS = {
  pending_assessment: 'warning',
  offer_made: 'info',
  accepted: 'primary',
  rejected: 'error',
  completed: 'success',
};

const STATUS_LABELS = {
  pending_assessment: 'Pending Assessment',
  offer_made: 'Offer Made',
  accepted: 'Accepted',
  rejected: 'Rejected',
  completed: 'Completed',
};

export default function TradeInView() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const { data: ticket, isLoading, refetch } = useGetTradeInByIdQuery(id);
  const [updateTradeIn, { isLoading: isUpdating }] = useUpdateTradeInMutation();
  const [completeTradeIn, { isLoading: isCompleting }] = useCompleteTradeInMutation();
  
  const { data: productsData } = useGetProductsQuery({ limit: 1000 });
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';

  const [assessmentNotes, setAssessmentNotes] = useState('');
  const [finalOfferValue, setFinalOfferValue] = useState(0);
  const [status, setStatus] = useState('pending_assessment');
  const [productId, setProductId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (ticket) {
      setAssessmentNotes(ticket.assessmentNotes || '');
      setFinalOfferValue(ticket.finalOfferValue || ticket.estimatedValue || 0);
      setStatus(ticket.status || 'pending_assessment');
      setProductId(ticket.productId || '');
      setPaymentMethod(ticket.paymentMethod || 'cash');
    }
  }, [ticket]);

  if (isLoading) return <Typography>Loading...</Typography>;
  if (!ticket) return <Typography>Trade-in ticket not found.</Typography>;

  const handleUpdate = async () => {
    setErrorMsg('');
    try {
      await updateTradeIn({
        id,
        assessmentNotes,
        finalOfferValue: parseFloat(finalOfferValue) || 0,
        status,
        productId,
        paymentMethod
      }).unwrap();
    } catch (err) {
      setErrorMsg(err.data?.message || 'Failed to update trade-in');
    }
  };

  const handleComplete = async () => {
    setErrorMsg('');
    if (!productId) {
      setErrorMsg('Please select a mapped inventory product before completing.');
      return;
    }
    if (parseFloat(finalOfferValue) <= 0) {
      setErrorMsg('Final offer value must be greater than zero.');
      return;
    }
    try {
      await updateTradeIn({
        id,
        assessmentNotes,
        finalOfferValue: parseFloat(finalOfferValue) || 0,
        status: 'accepted',
        productId,
        paymentMethod
      }).unwrap();
      
      await completeTradeIn(id).unwrap();
      refetch();
    } catch (err) {
      setErrorMsg(err.data?.message || 'Failed to complete buyback');
    }
  };

  const isCompleted = ticket.status === 'completed';

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {ticket.ticketNumber} - Trade-In Assessment
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/trade-ins')}>
          Back to List
        </Button>
      </Box>

      {errorMsg && (
        <Alert severity="error" sx={{ mb: 3 }}>{errorMsg}</Alert>
      )}

      {isCompleted && (
        <Alert severity="success" sx={{ mb: 3 }} icon={<CheckCircleIcon />}>
          This trade-in has been completed. The device is now in inventory and the customer has been paid via {ticket.paymentMethod}.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left Column: Device & Customer Info */}
        <Grid size={{xs:12, md:5}}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Status</Typography>
              <Chip 
                label={STATUS_LABELS[ticket.status]} 
                color={STATUS_COLORS[ticket.status]} 
              />
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" gutterBottom>Customer Details</Typography>
            <Typography variant="body1"><strong>Name:</strong> {ticket.customer?.name}</Typography>
            <Typography variant="body1"><strong>Phone:</strong> {ticket.customer?.phone}</Typography>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" gutterBottom>Intake Details</Typography>
            <Typography variant="body1"><strong>Model:</strong> {ticket.deviceModel}</Typography>
            <Typography variant="body1"><strong>IMEI:</strong> {ticket.deviceImei || '-'}</Typography>
            <Typography variant="body1"><strong>Condition:</strong> {ticket.deviceCondition}</Typography>
            <Typography variant="body1"><strong>Accessories:</strong> {ticket.accessoriesIncluded || '-'}</Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              <strong>Estimated Value:</strong> {parseFloat(ticket.estimatedValue).toFixed(2)} {currency}
            </Typography>
          </Paper>
        </Grid>

        {/* Right Column: Assessment & Completion */}
        <Grid size={{xs:12, md:7}}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Assessment & Offer</Typography>
            
            <Grid container spacing={2}>
              <Grid size={{xs:12}}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Technician Assessment Notes"
                  value={assessmentNotes}
                  onChange={(e) => setAssessmentNotes(e.target.value)}
                  disabled={isCompleted}
                />
              </Grid>

              <Grid size={{xs:12, md:6}}>
                <TextField
                  fullWidth
                  type="number"
                  label={`Final Offer Value (${currency})`}
                  value={finalOfferValue}
                  onChange={(e) => setFinalOfferValue(e.target.value)}
                  disabled={isCompleted}
                />
              </Grid>

              <Grid size={{xs:12, md:6}}>
                <FormControl fullWidth disabled={isCompleted}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={status}
                    label="Status"
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <MenuItem value="pending_assessment">Pending Assessment</MenuItem>
                    <MenuItem value="offer_made">Offer Made</MenuItem>
                    <MenuItem value="accepted">Accepted</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>Buyback Completion</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Map this trade-in to an existing inventory product and select a payment method to complete the buyback.
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{xs:12}}>
                <FormControl fullWidth disabled={isCompleted}>
                  <InputLabel>Map to Inventory Product</InputLabel>
                  <Select
                    value={productId}
                    label="Map to Inventory Product"
                    onChange={(e) => setProductId(e.target.value)}
                  >
                    <MenuItem value=""><em>-- Select Product --</em></MenuItem>
                    {productsData?.products?.map(p => (
                      <MenuItem key={p.id} value={p.id}>{p.name} {p.sku ? `(${p.sku})` : ''}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{xs:12}}>
                <FormControl fullWidth disabled={isCompleted}>
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    value={paymentMethod}
                    label="Payment Method"
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  >
                    <MenuItem value="cash">Cash (Deduct from Register)</MenuItem>
                    <MenuItem value="bank">Bank Transfer</MenuItem>
                    <MenuItem value="store_credit">Store Credit</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {!isCompleted && (
              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleUpdate}
                  disabled={isUpdating || isCompleting}
                >
                  Save Changes
                </Button>
                <Button 
                  variant="contained" 
                  color="success"
                  onClick={handleComplete}
                  disabled={isUpdating || isCompleting}
                >
                  Complete Buyback
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
